import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutGrid, Factory, TrendingUp, ListTree, Boxes, Truck, ScrollText, ShoppingBag, ClipboardList, CalendarDays, Gauge, Network, ClipboardCheck } from "lucide-react";
import { useData } from "../lib/DataContext";
import { signOut } from "../lib/api";
import { isLow } from "./shared";
import { APP_VERSION } from "../lib/version";
import { itemAlerts } from "../pages/Items";
import HoverCardLayer from "./HoverCardLayer";
import POEmailPrompt from "./POEmailPrompt";
import { useEffect, useState } from "react";
import POAlertPrompts from "./POAlertPrompts";
import { ChevronDown, ShieldCheck } from "lucide-react";
import { AuthContextUser } from "./AuthGate";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutGrid, id: "dashboard" },
  { to: "/reconciliation", label: "Reconcile", icon: ClipboardCheck, id: "reconciliation" },
  { to: "/produce", label: "Produce", icon: Factory, id: "produce" },
  { to: "/schedule", match: "/schedule", label: "Schedule", icon: CalendarDays, id: "schedule", children: [
    { to: "/schedule", label: "Weekly Schedule", id: "schedule-weekly" },
    { to: "/schedule/90", label: "90-Day Schedule", id: "schedule-90" },
    { to: "/schedule/planning", label: "Production Planning", id: "schedule-planning" },
  ] },
  { to: "/forecast", label: "Forecast", icon: TrendingUp, id: "forecast" },
  { to: "/boms", label: "BOMs", icon: ListTree, id: "boms" },
  { to: "/ordering/suggested", match: "/ordering", label: "Ordering", icon: ClipboardList, id: "ordering", children: [
    { to: "/ordering/suggested", label: "Ordering Required", id: "ordering-required" },
    { to: "/ordering", label: "Purchase Orders", id: "ordering-pos" },
    { to: "/ordering/template", label: "Order Templates", id: "ordering-template" },
  ] },
  { to: "/items", match: "/items", label: "Items", icon: Boxes, id: "items", admin: true, children: [
    { to: "/items", label: "Item Master", id: "items-master" },
    { to: "/items/bundles", label: "Kits / Bundles", id: "items-bundles", flag: "enable_bundles" },
  ] },
  { to: "/suppliers", label: "Suppliers", icon: Truck, id: "suppliers", admin: true },
  { to: "/log", label: "Audit Log", icon: ScrollText, id: "log", admin: true },
  { to: "/shopify", label: "Integrations", icon: ShoppingBag, id: "shopify", admin: true },
  { to: "/controls", label: "Controls", icon: Gauge, id: "controls", admin: true },
  { to: "/bom-graph", label: "BOM Graph", icon: Network, id: "bom-graph", admin: true },
  { to: "/admin/access", label: "Super Admin", icon: ShieldCheck, id: "super-admin", admin: true, superOnly: true },
];

function SyncIndicator() {
  const { lastSync, error } = useData();
  const color = error ? "bg-[#EF4444]" : "bg-[#10B981]";
  return (
    <div data-testid="sync-status-indicator" className="flex items-center gap-2 font-mono text-[11px] text-[#9CA3AF]">
      <span className="relative flex h-2.5 w-2.5">
        <span className={`absolute inline-flex h-full w-full rounded-full ${color} sync-ring`} />
        <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${color}`} />
      </span>
      {error ? "OFFLINE" : `LIVE · ${lastSync ? lastSync.toLocaleTimeString() : "…"}`}
    </div>
  );
}

export function OrderBadge({ testId, className = "", kind = "all" }) {
  const { ordering } = useData();
  if (!ordering) return null;
  const late = ordering.late_pos || 0;
  const n = kind === "late" ? late : kind === "reorder" ? ordering.due_soon : ordering.due_soon + late;
  if (!n) return null;
  const red = kind === "late" || ordering.overdue || late;
  return <span data-testid={testId} title={`${ordering.overdue} reorders overdue · ${ordering.due_soon} due within ${ordering.soon_days} days · ${late} late POs`}
    className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-sm text-white ${red ? "bg-[#EF4444]" : "bg-[#0EA5E9]"} ${className}`}>{n}</span>;
}

export function ReconBadge({ testId, className = "" }) {
  const { items } = useData();
  const n = items.filter((i) => i.needs_reconciliation).length;
  if (!n) return null;
  return <span data-testid={testId} className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-sm text-[#090D16] bg-[#A78BFA] ${className}`}>{n}</span>;
}

export function NoBomBadge({ testId, className = "" }) {
  const { items, boms, suppliers } = useData();
  const a = itemAlerts(items, boms, suppliers);
  const parts = [["missing BOM", a["missing-bom"].size], ["no unit cost", a["no-cost"].size], ["no vendor", a["no-vendor"].size], ["no external SKU", a["no-ext-sku"].size]].filter(([, c]) => c);
  const n = parts.reduce((s, [, c]) => s + c, 0);
  if (!n) return null;
  return <span data-testid={testId} title={`Item Master alerts: ${parts.map(([l, c]) => `${c} ${l}`).join(" · ")}`} className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-sm text-[#090D16] bg-[#F59E0B] ${className}`}>{n}</span>;
}

export function PlanBadge({ testId, className = "" }) {
  const { plans } = useData();
  const n = plans?.expired || 0;
  if (!n) return null;
  return <span data-testid={testId} data-tip={`${n} Production Plan(s) past maturation date`} className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-sm text-white bg-[#EF4444] ${className}`}>{n}</span>;
}

export function PlanDelayBadge({ testId, className = "" }) {
  const { plans } = useData();
  const n = plans?.delayed || 0;
  if (!n) return null;
  return <span data-testid={testId} data-tip={`${n} Production Plan(s) with missed milestones · ${plans.missed_steps} step(s) past Start / Order By`} className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-sm text-[#090D16] bg-[#FB923C] ${className}`}>{n}</span>;
}

export function BomReviewBadge({ testId, className = "" }) {
  const { boms } = useData();
  const n = boms.filter((b) => b.needs_review).length;
  if (!n) return null;
  return <NavLink to="/boms?review=1" data-testid={testId} data-tip={`${n} BOM(s) flagged Copy - Needs Review`} onClick={(e) => e.stopPropagation()} className={`font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-sm text-[#F59E0B] bg-[#090D16] border border-[#F59E0B] ${className}`}>{n}</NavLink>;
}

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-4 h-12 rounded-md text-sm font-medium transition-colors ${isActive ? "bg-[#F59E0B] text-[#090D16]" : "text-[#9CA3AF] hover:bg-[#172033] hover:text-white"}`;

export default function Layout() {
  const { items, policy } = useData();
  const { pathname } = useLocation();
  const sectionActive = (n) => n.match && pathname.startsWith(n.match);
  const low = items.filter(isLow).length;
  const [openGroups, setOpenGroups] = useState({});
  const groupOpen = (n) => openGroups[n.id] ?? sectionActive(n);
  const nav = NAV.filter((n) => !n.superOnly || AuthContextUser.current?.is_super_admin);
  const [pop, setPop] = useState(null);
  useEffect(() => { setPop(null); }, [pathname]);
  const popItem = nav.find((n) => n.id === pop);
  return (
    <div className="grain min-h-screen flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-[#0B111D] border-r border-[#1E293B] sticky top-0 h-screen p-4 z-10">
        <div className="px-2 py-4 mb-4">
          <div className="font-display font-black text-2xl tracking-tight" data-testid="brand-name">FLOW<span className="text-[#F59E0B]">FORGE</span></div>
          <div className="flex items-center gap-2 mt-2"><div className="h-1 w-16 hazard-stripe" /><span data-testid="app-version" className="font-mono text-[10px] text-[#6B7280]">{APP_VERSION}</span>
            <button data-testid="logout-btn" onClick={() => signOut().then(() => window.location.assign("/"))} className="ml-auto font-mono text-[10px] text-[#9CA3AF] hover:text-white">SIGN OUT</button></div>
        </div>
        <nav className="flex flex-col gap-1 flex-1 min-h-0 overflow-y-auto pr-1 -mr-1" data-testid="sidebar-nav">
          {nav.map((n, i) => (
            <div key={n.to}>
              {n.admin && !nav[i - 1].admin && <div className="font-mono text-[10px] tracking-[0.25em] text-[#6B7280] px-4 pt-6 pb-2">ADMIN</div>}
              <NavLink to={n.to} end={n.to === "/"} className={(s) => linkCls({ isActive: s.isActive || sectionActive(n) })} data-testid={`nav-${n.id}`}>
                <n.icon className="w-5 h-5" /> {n.label}
                {n.id === "dashboard" && low > 0 && <span data-testid="nav-low-stock-count" title={`${low} item${low > 1 ? "s" : ""} below par`} className="ml-auto font-mono text-[10px] bg-[#EF4444] text-white px-1.5 py-0.5 rounded-sm">{low}</span>}
                {n.id === "ordering" && <OrderBadge testId="nav-ordering-badge" className={n.children ? "ml-auto mr-8" : "ml-auto"} />}
                {n.id === "reconciliation" && <ReconBadge testId="nav-reconciliation-badge" className="ml-auto" />}
                {n.id === "items" && <NoBomBadge testId="nav-items-nobom-badge" className="ml-auto mr-8" />}
                {n.id === "boms" && <BomReviewBadge testId="nav-boms-review-badge" className="ml-auto" />}
                {n.id === "schedule" && <PlanBadge testId="nav-schedule-plan-badge" className="ml-auto mr-8" />}
              </NavLink>
              {n.children && (
                <button type="button" data-testid={`nav-${n.id}-toggle`} onClick={() => setOpenGroups((g) => ({ ...g, [n.id]: !groupOpen(n) }))}
                  data-tip={groupOpen(n) ? "Collapse" : "Expand"} className="float-right -mt-10 mr-1 h-8 w-8 grid place-items-center rounded text-[#6B7280] hover:text-white relative z-[1]">
                  <ChevronDown className={`w-4 h-4 transition-transform ${groupOpen(n) ? "" : "-rotate-90"}`} /></button>
              )}
              {n.children && groupOpen(n) && (
                <div className="ml-6 mt-1 mb-1 border-l border-[#1E293B] pl-3 flex flex-col gap-1">
                  {n.children.filter((c) => !c.flag || policy?.[c.flag] !== false).map((c) => (
                    <NavLink key={c.to} to={c.to} end data-testid={`nav-${c.id}`}
                      className={({ isActive }) => `h-10 px-3 flex items-center rounded-md text-sm ${isActive ? "text-[#F59E0B] bg-[#172033]" : "text-[#9CA3AF] hover:text-white"}`}>
                      {c.label}{c.id === "ordering-required" && <OrderBadge kind="reorder" testId="nav-ordering-required-badge" className="ml-auto" />}{c.id === "ordering-pos" && <OrderBadge kind="late" testId="nav-late-po-badge" className="ml-auto" />}{c.id === "schedule-planning" && <><PlanDelayBadge testId="nav-planning-delay-badge" className="ml-auto" /><PlanBadge testId="nav-planning-plan-badge" className="ml-1" /></>}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        <div className="mt-auto px-2"><SyncIndicator /></div>
      </aside>
      <div className="flex-1 min-w-0 relative z-[1]">
        <header className="lg:hidden sticky top-0 z-20 flex items-center justify-between px-4 h-14 bg-[#0B111D]/90 backdrop-blur-lg border-b border-[#1E293B]">
          <div className="font-display font-black text-xl">FLOW<span className="text-[#F59E0B]">FORGE</span></div>
          <SyncIndicator />
        </header>
        <main className="px-4 sm:px-8 lg:px-12 py-8 pb-28 lg:pb-12 max-w-[1600px]"><Outlet /></main>
        <HoverCardLayer />
        <POEmailPrompt />
        <POAlertPrompts />
        {popItem && (
          <>
            <div className="lg:hidden fixed inset-0 z-20 bg-black/40" onClick={() => setPop(null)} data-testid="mnav-popout-backdrop" />
            <div className="lg:hidden fixed bottom-16 inset-x-2 z-30 rounded-md border border-[#334155] bg-[#0B111D] p-2 shadow-2xl rise" data-testid={`mnav-popout-${popItem.id}`}>
              <div className="font-mono text-[10px] tracking-[0.25em] text-[#6B7280] px-3 pt-1 pb-2">{popItem.label.toUpperCase()}</div>
              {popItem.children.filter((c) => !c.flag || policy?.[c.flag] !== false).map((c) => (
                <NavLink key={c.to} to={c.to} end data-testid={`mnav-sub-${c.id}`}
                  className={({ isActive }) => `h-12 px-3 flex items-center rounded-md text-sm ${isActive ? "text-[#F59E0B] bg-[#172033]" : "text-[#E5E7EB] hover:bg-[#172033]"}`}>{c.label}</NavLink>
              ))}
            </div>
          </>
        )}
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-20 bg-[#0B111D]/95 backdrop-blur-lg border-t border-[#1E293B] grid grid-flow-col auto-cols-[minmax(60px,1fr)] overflow-x-auto">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`mnav-${n.id}`}
              onClick={(e) => { if (n.children) { e.preventDefault(); setPop(pop === n.id ? null : n.id); } }}
              className={({ isActive }) => `relative flex flex-col items-center justify-center gap-1 h-16 text-[10px] font-medium ${isActive || sectionActive(n) || pop === n.id ? "text-[#F59E0B]" : "text-[#9CA3AF]"}`}>
              <n.icon className="w-5 h-5" />{n.label}
              {n.id === "ordering" && <OrderBadge testId="mnav-ordering-badge" className="absolute top-1 right-1" />}
              {n.id === "reconciliation" && <ReconBadge testId="mnav-reconciliation-badge" className="absolute top-1 right-1" />}
              {n.id === "items" && <NoBomBadge testId="mnav-items-nobom-badge" className="absolute top-1 right-1" />}
              {n.id === "schedule" && <PlanBadge testId="mnav-schedule-plan-badge" className="absolute top-1 right-1" />}
              {n.id === "schedule" && <PlanDelayBadge testId="mnav-schedule-delay-badge" className="absolute top-1 left-1" />}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}
